import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  notification,
  Input,
  Select,
  message,
  Segmented,
} from "antd";
import {
  ClearOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  fetchTasks,
  deleteTask,
  updateTask,
  notifyProjectUsersAboutTask,
  getTaskDefaultNotifyRecipients,
} from "../services/tasksAPI";
import { useAuth } from "../hooks/useAuth";
import CreateTaskModal from "../components/CreateTaskModal";
import UpdateTaskModal from "../components/UpdateTaskModal";
import ViewTaskModal from "../components/ViewTaskModal";
import TaskKanbanBoard from "../components/TaskKanbanBoard";
import TasksTableView, {
  TasksTableColumnSelector,
  DEFAULT_COLUMNS,
} from "../components/TasksTableView";
import NotifyUsersModal from "../components/NotifyUsersModal";
import { useSearchParams } from "react-router-dom";
import { getPreferredPageSize, savePreferredPageSize } from "../utils/paginationUtils";
import { PAYMENT_PROJECTS_LIST } from "../constants/paymentConstants";
import {
  getModuleSettings,
  saveModuleSettings,
  clearModuleSettings,
} from "../utils/filterStorageUtils";
import { applyDateFiltersToQueryParams } from "../utils/dateFilterUtils";
import QuickFilterButtons from "../components/QuickFilterButtons";
import { toggleQuickFilter } from "../utils/quickFilterUtils";
import {
  normalizeColumnSelectionOrder,
} from "../utils/columnOrderUtils";
import { exportTasksToPDF } from "../utils/pdfUtils";

const KANBAN_FETCH_LIMIT = 200;
const TASK_DATE_FILTER_KEYS = ["created_at", "due_date", "closed_at_date"];
const MANAGE_ROLES = ["owner", "project_manager", "field"];

const SORT_OPTIONS = [
  { value: "number", label: "Number" },
  { value: "title", label: "Title" },
  { value: "due_date", label: "Due Date" },
  { value: "priority", label: "Priority" },
  { value: "created_at", label: "Created Date" },
];

const buildTaskFiltersForAPI = (filters) =>
  applyDateFiltersToQueryParams(filters, TASK_DATE_FILTER_KEYS);

const Tasks = () => {
  const { selectedProject, currentUser, setCurrentUser, setSelectedProject } = useAuth();
  const projectId = selectedProject.project_id;
  const userRole = selectedProject.role;
  const canManageTasks = MANAGE_ROLES.includes(userRole);

  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isNotifyModalVisible, setIsNotifyModalVisible] = useState(false);
  const [taskToNotify, setTaskToNotify] = useState(null);
  const [notifyDefaultUserIds, setNotifyDefaultUserIds] = useState([]);
  const [notifyDefaultUserIdsLoading, setNotifyDefaultUserIdsLoading] = useState(false);
  const notifyDefaultRecipientsFetchSeqRef = useRef(0);
  const [notifyingTaskId, setNotifyingTaskId] = useState(null);
  const [exportPdfLoading, setExportPdfLoading] = useState(false);

  const queryClient = useQueryClient();
  const savedSettings = getModuleSettings(projectId, "tasks");
  const [filters, setFilters] = useState(savedSettings.filters || {});
  const [searchedColumns, setSearchedColumns] = useState(savedSettings.searchedColumns || []);
  const [searchTexts, setSearchTexts] = useState(savedSettings.searchTexts || {});
  const [masterSearch, setMasterSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const taskIdFromUrl = searchParams.get("task_id");
  const projectIdFromUrl = searchParams.get("project_id");
  const userProjectIds =
    currentUser?.projects?.map((p) => p.project_id).join(",") ?? "";
  const lastLoadedProjectId = useRef(projectId);
  const [loadedSettingsProjectId, setLoadedSettingsProjectId] =
    useState(projectId);
  const [sortField, setSortField] = useState(savedSettings.sortField || "number");
  const [sortOrder, setSortOrder] = useState(savedSettings.sortOrder || "asc");
  const [viewMode, setViewMode] = useState(savedSettings.viewMode || "list");
  const [tableKey, setTableKey] = useState(0);
  const [visibleColumns, setVisibleColumns] = useState(
    normalizeColumnSelectionOrder(
      currentUser?.task_columns || DEFAULT_COLUMNS,
      [
        "number",
        "title",
        "formatted_ball_in_court_users",
        "priority",
        "created_at",
        "due_date",
        "status",
        "closed_at_date",
        "action",
      ],
    ),
  );
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: getPreferredPageSize("tasks"),
    total: 0,
  });

  const isBoardView = viewMode === "board";

  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: [
      "tasks",
      projectId,
      "list",
      pagination.current,
      pagination.pageSize,
      filters,
      masterSearch,
      sortField,
      sortOrder,
    ],
    queryFn: () =>
      fetchTasks(
        projectId,
        pagination.current,
        pagination.pageSize,
        buildTaskFiltersForAPI(filters),
        masterSearch,
        sortField,
        sortOrder,
      ),
    enabled: !!projectId && !isBoardView && loadedSettingsProjectId === projectId,
    placeholderData: keepPreviousData,
  });

  const { data: boardData, isLoading: boardLoading } = useQuery({
    queryKey: [
      "tasks",
      projectId,
      "kanban",
      filters,
      masterSearch,
      sortField,
      sortOrder,
    ],
    queryFn: () =>
      fetchTasks(
        projectId,
        1,
        KANBAN_FETCH_LIMIT,
        buildTaskFiltersForAPI(filters),
        masterSearch,
        sortField,
        sortOrder,
      ),
    enabled: !!projectId && isBoardView && loadedSettingsProjectId === projectId,
    placeholderData: keepPreviousData,
  });

  const taskData = isBoardView ? boardData : listData;
  const isLoading = isBoardView ? boardLoading : listLoading;

  useEffect(() => {
    if (!isBoardView && listData?.total !== undefined) {
      setPagination((prev) => ({ ...prev, total: listData.total }));
    }
  }, [listData?.total, isBoardView]);

  useEffect(() => {
    if (!isBoardView) {
      setPagination((prev) => (prev.current === 1 ? prev : { ...prev, current: 1 }));
    }
  }, [filters, masterSearch, sortField, sortOrder, isBoardView]);

  useEffect(() => {
    if (lastLoadedProjectId.current === projectId) {
      saveModuleSettings(projectId, "tasks", {
        filters,
        searchedColumns,
        searchTexts,
        sortField,
        sortOrder,
        viewMode,
      });
    }
  }, [filters, searchedColumns, searchTexts, sortField, sortOrder, viewMode, projectId]);

  // Load settings when projectId changes; keep persist effect from writing
  // the previous project's state into the newly selected project.
  useEffect(() => {
    const settings = getModuleSettings(projectId, "tasks");
    setFilters(settings.filters || {});
    setSearchTexts(settings.searchTexts || {});
    setSearchedColumns(settings.searchedColumns || []);
    setSortField(settings.sortField || "number");
    setSortOrder(settings.sortOrder || "asc");
    setViewMode(settings.viewMode || "list");
    setMasterSearch("");
    setPagination((prev) => ({ ...prev, current: 1 }));
    setTableKey((prev) => prev + 1);
    lastLoadedProjectId.current = projectId;
    setLoadedSettingsProjectId(projectId);
  }, [projectId]);

  useEffect(() => {
    if (currentUser?.task_columns?.length) {
      setVisibleColumns(
        normalizeColumnSelectionOrder(currentUser.task_columns, [
          "number",
          "title",
          "formatted_ball_in_court_users",
          "priority",
          "created_at",
          "due_date",
          "status",
          "closed_at_date",
          "action",
        ]),
      );
    }
  }, [currentUser?.task_columns]);

  useEffect(() => {
    if (!projectIdFromUrl || !currentUser?.projects?.length) return;
    if (selectedProject?.project_id === projectIdFromUrl) return;
    const match = currentUser.projects.find(
      (p) => p.project_id === projectIdFromUrl,
    );
    if (match) {
      setSelectedProject(match);
    }
  }, [
    projectIdFromUrl,
    userProjectIds,
    selectedProject?.project_id,
    setSelectedProject,
  ]);

  useEffect(() => {
    if (taskIdFromUrl) {
      setSelectedTaskId(taskIdFromUrl);
      setIsViewModalVisible(true);
      const next = new URLSearchParams(searchParams);
      next.delete("task_id");
      next.delete("project_id");
      setSearchParams(next, { replace: true });
    }
  }, [taskIdFromUrl, searchParams, setSearchParams]);

  const deleteMutation = useMutation({
    mutationFn: deleteTask,
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: ["tasks", projectId] });
      setSelectedTaskId((current) => (current === deletedId ? null : current));
      setIsViewModalVisible(false);
      notification.success({ message: "Task deleted successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to delete task",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ taskId, status }) => updateTask(taskId, { status }, false),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: ["tasks", projectId] });
      queryClient.invalidateQueries({ queryKey: ["task", taskId] });
      notification.success({ message: "Status updated successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update status",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  const handleStatusChange = (taskId, status) => {
    statusMutation.mutate({ taskId, status });
  };

  const handleViewTask = (task) => {
    setSelectedTaskId(task.id);
    setIsViewModalVisible(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalVisible(false);
    setSelectedTaskId(null);
  };

  const handleUpdateClick = (task) => {
    setSelectedTask(task);
    setIsUpdateModalVisible(true);
  };

  const handleDeleteClick = (id) => {
    deleteMutation.mutate(id);
  };

  const handleNotifyUsers = async (task) => {
    const fetchSeq = ++notifyDefaultRecipientsFetchSeqRef.current;

    setTaskToNotify(task);
    setNotifyDefaultUserIds([]);
    setNotifyDefaultUserIdsLoading(true);
    setIsNotifyModalVisible(true);

    try {
      const ids = await getTaskDefaultNotifyRecipients(task.id);
      if (fetchSeq !== notifyDefaultRecipientsFetchSeqRef.current) return;
      setNotifyDefaultUserIds(ids);
    } catch {
      if (fetchSeq !== notifyDefaultRecipientsFetchSeqRef.current) return;
      setNotifyDefaultUserIds([]);
    } finally {
      if (fetchSeq === notifyDefaultRecipientsFetchSeqRef.current) {
        setNotifyDefaultUserIdsLoading(false);
      }
    }
  };

  const handleSendNotification = async (userIds) => {
    if (!taskToNotify) return;
    setNotifyingTaskId(taskToNotify.id);
    try {
      await notifyProjectUsersAboutTask(taskToNotify.id, userIds);
      message.success("Notification sent successfully");
      setIsNotifyModalVisible(false);
      setTaskToNotify(null);
    } catch (error) {
      notification.error({
        message: "Failed to send notification",
        description: error.response?.data?.detail || error.message,
      });
    } finally {
      setNotifyingTaskId(null);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
    savePreferredPageSize(newPagination.pageSize, "tasks");
  };

  const handleSort = (field, order) => {
    setSortField(field);
    setSortOrder(order);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleExportPDF = async () => {
    setExportPdfLoading(true);
    try {
      const total = taskData?.total || 0;
      if (!total) {
        notification.warning({ message: "No data to export" });
        return;
      }

      notification.info({
        message: "Exporting PDF...",
        description: "Fetching all records...",
        duration: 2,
      });

      const allTasksData = await fetchTasks(
        projectId,
        1,
        total,
        buildTaskFiltersForAPI(filters),
        masterSearch,
        sortField,
        sortOrder,
      );

      if (!allTasksData?.tasks?.length) {
        notification.warning({ message: "No data to export" });
        return;
      }

      await exportTasksToPDF(
        allTasksData.tasks,
        selectedProject?.project_name || "Project",
        selectedProject,
        { columnOrder: visibleColumns },
      );
      notification.success({ message: "PDF exported successfully" });
    } catch (error) {
      notification.error({
        message: "Failed to export PDF",
        description: error.message,
      });
    } finally {
      setExportPdfLoading(false);
    }
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setSearchTexts({});
    setSearchedColumns([]);
    setMasterSearch("");
    setSortField("number");
    setSortOrder("asc");
    setTableKey((prev) => prev + 1);
    clearModuleSettings(projectId, "tasks");
  };

  const handleQuickFilterToggle = (filterKey, kind) => {
    setFilters((prev) => toggleQuickFilter(prev, filterKey, kind));
    if (!isBoardView) {
      setPagination((prev) => ({ ...prev, current: 1 }));
    }
    setTableKey((prev) => prev + 1);
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setSelectedTaskId(null);
    setIsViewModalVisible(false);
  };

  return (
    <div className={isBoardView ? "flex flex-col h-[calc(100vh-5rem)]" : ""}>
      <h1 className="text-2xl font-bold mb-4 shrink-0">Tasks</h1>

      <div className="flex gap-3 mb-4 flex-wrap items-center shrink-0">
        {canManageTasks && (
          <Button
            type="primary"
            onClick={() => {
              if (PAYMENT_PROJECTS_LIST.includes(projectId)) {
                notification.warning({
                  message: "Your project has outstanding invoices.",
                  description: "Please complete payment to create new tasks.",
                });
                return;
              }
              setIsCreateModalVisible(true);
            }}
          >
            Create
          </Button>
        )}
        <QuickFilterButtons filters={filters} onToggle={handleQuickFilterToggle} />
        <Button
          icon={<DownloadOutlined />}
          loading={exportPdfLoading}
          onClick={handleExportPDF}
        >
          Export PDF
        </Button>
        {!isBoardView && (
          <TasksTableColumnSelector
            setCurrentUser={setCurrentUser}
            visibleColumns={visibleColumns}
            onColumnsChange={setVisibleColumns}
          />
        )}
        {isBoardView && (
          <>
            <Select
              value={sortField}
              onChange={setSortField}
              options={SORT_OPTIONS}
              style={{ width: 140 }}
              placeholder="Sort by"
            />
            <Select
              value={sortOrder}
              onChange={setSortOrder}
              options={[
                { value: "asc", label: "Ascending" },
                { value: "desc", label: "Descending" },
              ]}
              style={{ width: 130 }}
            />
          </>
        )}
        <Input.Search
          placeholder="Search across all fields..."
          onSearch={(value) => setMasterSearch(value)}
          style={{ width: 280 }}
          allowClear
        />
        <div className="ml-auto flex gap-3 items-center">
          <Segmented
            value={viewMode}
            onChange={handleViewModeChange}
            options={[
              {
                value: "list",
                icon: <UnorderedListOutlined />,
                title: "List view",
              },
              {
                value: "board",
                icon: <AppstoreOutlined />,
                title: "Board view",
              },
            ]}
          />
          <Button icon={<ClearOutlined />} onClick={handleClearAllFilters}>
            Clear All Filters
          </Button>
        </div>
      </div>

      {isBoardView ? (
        <div className="flex flex-1 min-h-0 w-full border border-gray-200 rounded-lg overflow-hidden bg-gray-50/80">
          <TaskKanbanBoard
            tasks={taskData?.tasks}
            total={taskData?.total || 0}
            loading={isLoading}
            selectedTaskId={selectedTaskId}
            sortField={sortField}
            sortOrder={sortOrder}
            canManageTasks={canManageTasks}
            currentUser={currentUser}
            notifyingTaskId={notifyingTaskId}
            updatingTaskId={statusMutation.isPending ? statusMutation.variables?.taskId : null}
            onSelect={handleViewTask}
            onUpdate={handleUpdateClick}
            onNotify={handleNotifyUsers}
            onDelete={handleDeleteClick}
            onStatusChange={handleStatusChange}
          />
        </div>
      ) : (
        <TasksTableView
          currentUser={currentUser}
          canManageTasks={canManageTasks}
          tasks={taskData?.tasks}
          total={taskData?.total || pagination.total}
          loading={isLoading}
          pagination={pagination}
          onTableChange={handleTableChange}
          filters={filters}
          setFilters={setFilters}
          searchedColumns={searchedColumns}
          setSearchedColumns={setSearchedColumns}
          searchTexts={searchTexts}
          setSearchTexts={setSearchTexts}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={handleSort}
          tableKey={tableKey}
          visibleColumns={visibleColumns}
          onView={handleViewTask}
          onUpdate={handleUpdateClick}
          onDelete={handleDeleteClick}
          onNotify={handleNotifyUsers}
          onStatusChange={handleStatusChange}
          statusUpdatePending={statusMutation.isPending}
          notifyingTaskId={notifyingTaskId}
        />
      )}

      <CreateTaskModal visible={isCreateModalVisible} onClose={() => setIsCreateModalVisible(false)} />

      {selectedTaskId && (
        <ViewTaskModal
          visible={isViewModalVisible}
          onClose={handleCloseViewModal}
          taskId={selectedTaskId}
        />
      )}

      {selectedTask && (
        <UpdateTaskModal
          visible={isUpdateModalVisible}
          onClose={() => {
            setIsUpdateModalVisible(false);
            setSelectedTask(null);
          }}
          task={selectedTask}
        />
      )}

      <NotifyUsersModal
        visible={isNotifyModalVisible}
        onClose={() => {
          notifyDefaultRecipientsFetchSeqRef.current += 1;
          setIsNotifyModalVisible(false);
          setTaskToNotify(null);
          setNotifyDefaultUserIds([]);
          setNotifyDefaultUserIdsLoading(false);
        }}
        folder={taskToNotify}
        itemName={taskToNotify?.title}
        defaultUserIds={notifyDefaultUserIds}
        defaultUserIdsLoading={notifyDefaultUserIdsLoading}
        onNotify={handleSendNotification}
      />
    </div>
  );
};

export default Tasks;
